/* THE FOUR SEAT CONSOLES.
 *
 * They are genuinely different screens, not one screen with a different title. That
 * is the product's argument: the same gym looks different depending on whose job you
 * are doing, and a demo where every seat lands on the same dashboard has thrown it
 * away. Each seat's landing screen is named in seats.ts.
 *
 * TWO DEPARTURES FROM THE FIGMA, both because the design showed numbers this product
 * does not have, and a demo that invents them is a demo that lies:
 *
 *   CSAT 4.2/5        -> ANCHOR COVERAGE. There is no survey data. There IS the thing
 *                        the company is actually about: how many members are known by
 *                        name to somebody here. It is the better number.
 *   Upcoming renewals -> CARE TRACK REVIEWS. No renewal dates exist in the data.
 *                        Suppression review dates do, and a review falling due is the
 *                        one piece of Care Track admin that must not be missed.
 *
 * SUPPRESSION IS ENFORCED IN THE SOURCE, not here. Anyone on Care Track or Hold is out
 * of the risk list, the anchor list and every count on these screens, and is never
 * shown a score. They appear in exactly one place: the reviews panel, which exists so
 * they cannot be forgotten.
 */
import { useState } from 'react';
import { useAsync, useSource } from '../data/SourceProvider.js';
import type { DashboardPayload } from '../data/contract.js';
import { NoneRow, Panel, Stat } from '../components/Panel.js';
import { Avatar } from '../components/Avatar.js';
import { AnchorCapture } from './home/AnchorCapture.js';
import { LifecycleStrip } from './home/Lifecycle.js';
import { PipelinePanel } from './home/Pipeline.js';
import { PeoplePanel, riskEyebrow } from './home/Panels.js';
import { fmt } from '../lib/fmt.js';
import { go } from '../routes.js';
import { gymToday } from '../lib/today.js';
import { HOLD_PROMPT_DAYS, type Member, type Role } from '@acrostic/model';
import type { ScreenProps } from '../App.js';

/** One hook, four consoles. The seat decides the scope; the source does the work. */
function useConsole(seat: Role): {
  data: DashboardPayload | null; error: Error | null; reload: () => void;
} {
  const source = useSource();
  const [epoch, setEpoch] = useState(0);
  const { data, error } = useAsync(() => source.getDashboard(seat), [source, seat, epoch]);
  return { data, error, reload: () => setEpoch((n) => n + 1) };
}

function whyQuiet(m: Member): string {
  return m.risk?.why?.join(' · ') || m.risk?.guidance || 'Drifting from their own rhythm';
}

/* One sentence saying what this seat's screen is for. It carried the names/counts
   switch until that question was settled; the sentence is the part that was always
   doing the work. */
function TopBar({ children }: { children: React.ReactNode }) {
  return (
    <div className="toolbar">
      <span className="t-caption muted">{children}</span>
    </div>
  );
}

/* ---- SUPPORT COACH. The seat the product is built around. ---- */
export function HomeSales({ user }: ScreenProps) {
  const { data, error, reload } = useConsole('sales');
  const [anchoring, setAnchoring] = useState<{ m: Member; self: boolean } | null>(null);

  if (error) return <NoneRow>Could not read the console. {error.message}</NoneRow>;
  if (!data) return <NoneRow>Reading the gym…</NoneRow>;

  return (
    <>
      {/* THE STRAPLINE IS GONE, the warning is not. "Acrostic reads the people in
          your book, not the totals" told a support coach something about the product
          rather than about their morning, every time they opened it.

          What is left only appears when it is true and is worth interrupting for: a
          login nobody is assigned to is reading the WHOLE GYM's numbers as if they
          were their own book, and a console that has quietly widened its scope is a
          console reporting somebody else's work. */}
      {!data.scoped && (
        <TopBar>
          This login is not on the roster yet, so it is showing the whole gym rather
          than your own book.
        </TopBar>
      )}

      <div className="grid2">
        <PeoplePanel
         
          eyebrow={riskEyebrow(data.suppressedCount, data.onboardingCount)}
          title="Going quiet"
          people={data.goingQuiet.slice(0, 6)}
          line={whyQuiet}
          empty="Nobody in your book is drifting."
          right={<a className="linkish t-label-small" href="#/members">All {fmt.num(data.bookSize)}</a>}
        />

        {/* KNOWN BY NOBODY, made actionable. This panel named the people nobody here
            knows and then stopped, which made the product's central gap a thing you
            could read and not a thing you could close. It is the highest-value place
            in the app to record an anchor: the coach is already looking at the list
            of people it is missing for. */}
        <PeoplePanel
         
          eyebrow="Known by nobody, and not already above"
          title="Nobody's anchor"
          people={data.unanchored.slice(0, 6)}
          line={(m) => `No named anchor on record · joined ${fmt.date(m.joinedAt)}`}
          empty="Everybody in your book is known by somebody."
          scored={false}
          actions={(m) => (
            <>
              <button type="button" className="btn t-label-small"
                onClick={() => setAnchoring({ m, self: false })}>Who knows them?</button>
              <button type="button" className="btn t-label-small"
                title="You know them. You will still be asked what makes it two-way."
                onClick={() => setAnchoring({ m, self: true })}>I know them</button>
            </>
          )}
        />
      </div>

      <LifecycleStrip />
      <PipelinePanel rows={data.followUps} onChanged={reload} />
      <Numbers data={data} />

      {anchoring && (
        <AnchorCapture
          m={anchoring.m}
          prefill={anchoring.self ? { name: user.name, role: 'Support coach' } : undefined}
          onClose={() => setAnchoring(null)}
          onDone={() => { setAnchoring(null); reload(); }}
        />
      )}
    </>
  );
}

/* ---- COACH. "Who is in my room today", not "who is in the pipeline". ---- */
export function HomeCoach() {
  const { data, error, reload } = useConsole('coach');

  if (error) return <NoneRow>Could not read the console. {error.message}</NoneRow>;
  if (!data) return <NoneRow>Reading the gym…</NoneRow>;

  return (
    <>
      <div className="grid2">
        <PeoplePanel
         
          eyebrow={riskEyebrow(data.suppressedCount, data.onboardingCount)}
          title="Going quiet"
          people={data.goingQuiet.slice(0, 6)}
          line={whyQuiet}
          empty="Nobody of yours is drifting."
          right={<a className="linkish t-label-small" href="#/coach">My PT clients</a>}
        />
        <PeoplePanel
         
          eyebrow="Known by nobody"
          title="Nobody's anchor"
          people={data.unanchored.slice(0, 6)}
          line={(m) => `No named anchor on record · joined ${fmt.date(m.joinedAt)}`}
          empty="Everybody of yours is known by somebody."
          scored={false}
        />
      </div>

      <LifecycleStrip />
      <PipelinePanel rows={data.followUps} onChanged={reload} />
      <Numbers data={data} />
    </>
  );
}

/* ---- MANAGER. The day IS the queue, so approvals are folded in rather than beside. ---- */
export function HomeManager() {
  const source = useSource();
  const { data, error, reload } = useConsole('manager');
  const [epoch, setEpoch] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const approvals = useAsync(() => source.getApprovals(), [source, epoch]);

  if (error) return <NoneRow>Could not read the console. {error.message}</NoneRow>;
  if (!data) return <NoneRow>Reading the gym…</NoneRow>;

  async function decide(id: string, decision: 'approved' | 'rejected') {
    setBusy(id);
    await source.decideApproval(id, decision);
    setBusy(null);
    setEpoch((n) => n + 1);
    reload();
  }

  return (
    <>
      <Panel
        eyebrow="Waiting on you"
        title="Approvals"
        right={<a className="linkish t-label-small" href="#/approvals">The full queue</a>}
      >
        {!approvals.data?.total ? <NoneRow>Nothing waiting.</NoneRow> : (
          <div className="rows">
            {approvals.data.items.slice(0, 5).map((a) => (
              <div className="row" key={a.id}>
                <div className="who">
                  <div className="n t-label-large">{a.summary}</div>
                  <div className="s t-caption muted">
                    {a.kind.replace(/_/g, ' ')} · asked by {a.requestedBy} · {fmt.ago(a.requestedAt)}
                  </div>
                </div>
                <button type="button" className="btn primary t-label-small" disabled={busy === a.id}
                  onClick={() => void decide(a.id, 'approved')}>Approve</button>
                <button type="button" className="btn t-label-small" disabled={busy === a.id}
                  onClick={() => void decide(a.id, 'rejected')}>Reject</button>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* ABOVE COMING BACK, and that order is the ruling (PRD 7): anybody who has
          raised cancelling sits above everything else, ahead of members whose
          hold ends this week. Renders nothing when the list is empty. */}
      <Cancellations data={data} />
      <ComingBack data={data} />
      <div className="grid2">
        <PeoplePanel
         
          eyebrow={riskEyebrow(data.suppressedCount, data.onboardingCount)}
          title="Going quiet"
          people={data.goingQuiet.slice(0, 6)}
          line={whyQuiet}
          empty="Nobody is drifting."
          right={<a className="linkish t-label-small" href="#/members">All members</a>}
        />
        <CareReviews data={data} />
      </div>

      <LifecycleStrip />
      <PipelinePanel rows={data.followUps} onChanged={reload} />
      <Numbers data={data} />
    </>
  );
}

/* ---- OWNER. The one seat where a count may lead, and only if it opens its names. ---- */
export function Dashboard() {
  const { data, error, reload } = useConsole('gym_admin');

  if (error) return <NoneRow>Could not read the console. {error.message}</NoneRow>;
  if (!data) return <NoneRow>Reading the gym…</NoneRow>;
  const s = data.stats;

  return (
    <>
      <section className="panel stats">
        <Stat value={fmt.num(s['members'] ?? 0)} label="Current members" onOpen={() => go('/members')} />
        <Stat value={fmt.num(s['goingQuiet'] ?? 0)} label="Going quiet" onOpen={() => go('/members')} />
        <Stat value={fmt.num(s['openLeads'] ?? 0)} label="Open leads" onOpen={() => go('/leads')} />
        {/* The one money number on the screen, and it is a sum of what members
            actually pay rather than a projection. */}
        <Stat value={fmt.money(s['mrrCents'] ?? 0)} label="Monthly recurring" />
        <Stat
          value={data.coverage.of ? Math.round((data.coverage.covered / data.coverage.of) * 100) + '%' : '—'}
          label="Known by somebody"
          onOpen={() => go('/segments')}
        />
        <Stat value={fmt.num(s['visits30d'] ?? 0)} label="Visits, last 30 days" />
      </section>

      {/* ABOVE COMING BACK, and that order is the ruling (PRD 7): anybody who has
          raised cancelling sits above everything else, ahead of members whose
          hold ends this week. Renders nothing when the list is empty. */}
      <Cancellations data={data} />
      <ComingBack data={data} />
      <div className="grid2">
        <PeoplePanel
         
          eyebrow={riskEyebrow(data.suppressedCount, data.onboardingCount)}
          title="Going quiet"
          people={data.goingQuiet.slice(0, 6)}
          line={whyQuiet}
          empty="Nobody is drifting."
          right={<a className="linkish t-label-small" href="#/members">All members</a>}
        />
        <CareReviews data={data} />
      </div>

      <LifecycleStrip />
      <PipelinePanel rows={data.followUps} onChanged={reload} />
      <Numbers data={data} />
    </>
  );
}

/* ---- The two panels every console ends on ---- */

/* COMING BACK: the prompt a day or two before a hold ends.
 *
 * Anuj, 2026-09-15: "a day or two before the suspension we should be prompted to ask
 * them (hows the injury, how was vacation, etc, based on the notes)".
 *
 * It is its own panel rather than a pill in the list below, because it is the only
 * part of suppression admin that is TIME-CRITICAL IN THE OTHER DIRECTION. Everything
 * in Care Track and Hold reviews is a thing that has already slipped; this is the one
 * thing there is still time to do well. Sorted into the overdue list it would sit at
 * the bottom, under people whose review lapsed months ago, and be read last.
 *
 * THE REASON IS THE POINT, so it is printed large rather than as a caption. "Knee
 * surgery on the 20th" is the question — "how is the knee?" — and a panel that said
 * only "Priya's hold ends Thursday" would produce "your membership resumes Thursday",
 * which is the thing this exists to prevent.
 *
 * Computed here, from `due`, rather than asked of the source: the source already
 * sends every hold with its date, and the screen deciding which of them are worth
 * saying first needs no new field on the contract.
 */
function comingBack(rows: DashboardPayload['careReviews'], today: string) {
  return rows.filter(({ member, due, overdueBy }) =>
    member.suppression?.kind === 'hold'
    && due != null
    && (overdueBy ?? 0) <= 0                              // not yet lapsed
    && daysBetweenISO(today, due) <= HOLD_PROMPT_DAYS);
}

/** Whole days from a to b, both gym-dates. Negative when b is behind a. */
function daysBetweenISO(a: string, b: string): number {
  const ms = Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z');
  return Math.round(ms / 86400000);
}

/* CANCELLATION INITIATED (PRD 4). Above everything, Coming back included.
 *
 * The most urgent people in the building, and this panel is built to refuse the two
 * things that would spoil it. NO SCORE AND NO TIER, on any seat: they are frequently
 * Green when they ask, and printing the number invites somebody to weigh it against
 * the ask. And what they said prints WHERE THE NUMBER WOULD HAVE BEEN, in the words
 * it was heard in, because "at risk" is a category and "asked what the notice period
 * is, at the desk after class" is a person.
 *
 * The badge counts days waiting rather than naming a state, because nothing here
 * expires: a nine-day-old ask is not stale, it is nine days of silence. */
function Cancellations({ data }: { data: DashboardPayload }) {
  const today = gymToday();
  if (!data.cancellations.length) return null;
  return (
    <Panel
      eyebrow="The Owner speaks to these members. Nothing automated reaches them."
      title="Asked about cancelling"
    >
      <div className="rows">
        {data.cancellations.map((m) => {
          const c = m.cancellation!;
          const days = daysBetweenISO(c.askedAt, today);
          return (
            <button key={m.id} type="button" className="row clickable"
              onClick={() => go('/members/' + m.id)}>
              <Avatar name={m.name} photoUrl={m.photoUrl} size="sm" />
              <div className="who">
                <div className="n t-label-large">{m.name}</div>
                <div className="s t-body-small">{c.asked}</div>
                <div className="s t-caption muted">Heard by {c.recordedBy}</div>
              </div>
              <span className="pill risk t-label-small">
                {days <= 0 ? 'asked today' : days === 1 ? 'asked yesterday' : `${days}d waiting`}
              </span>
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

function ComingBack({ data }: { data: DashboardPayload }) {
  const today = gymToday();
  const rows = comingBack(data.careReviews, today);
  if (!rows.length) return null;
  return (
    <Panel eyebrow="Ask before they are back" title="Coming back this week">
      <div className="rows">
        {rows.map(({ member, due }) => {
          const inDays = daysBetweenISO(today, due!);
          return (
            <button key={member.id} type="button" className="row clickable"
              onClick={() => go('/members/' + member.id)}>
              <Avatar name={member.name} photoUrl={member.photoUrl} size="sm" />
              <div className="who">
                <div className="n t-label-large">{member.name}</div>
                <div className="s t-body-small">
                  {member.suppression?.reason
                    || 'No reason was written down, so there is nothing to ask about.'}
                </div>
              </div>
              <span className="pill good t-caption">
                {inDays <= 0 ? 'back today' : inDays === 1 ? 'back tomorrow' : `back in ${inDays}d`}
              </span>
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

function CareReviews({ data }: { data: DashboardPayload }) {
  /* The ones ComingBack is already showing come out, so the same person is not on
     the screen twice saying two different things. */
  const soon = new Set(comingBack(data.careReviews, gymToday()).map((r) => r.member.id));
  const rows = data.careReviews.filter((r) => !soon.has(r.member.id)).slice(0, 6);
  return (
    <Panel
      eyebrow="Away, not gone"
      title="Care Track and Hold reviews"
    >
      {!rows.length ? <NoneRow>Nobody is on Care Track or Hold.</NoneRow> : (
        <div className="rows">
          {rows.map(({ member, due, overdueBy }) => (
            <button key={member.id} type="button" className="row clickable"
              onClick={() => go('/members/' + member.id)}>
              <Avatar name={member.name} photoUrl={member.photoUrl} size="sm" />
              <div className="who">
                <div className="n t-label-large">{member.name}</div>
                <div className="s t-caption muted">
                  {member.suppression?.kind === 'hold' ? 'On hold' : 'Care Track'}
                  {member.suppression?.reason ? ' · ' + member.suppression.reason : ''}
                </div>
              </div>
              {/* A NULL REVIEW DATE IS THE WORST CASE, not a tidy absence: it is a
                  member who can be forgotten. It is said loudly rather than dashed. */}
              {due == null
                ? <span className="pill warn t-caption">No review date</span>
                : (overdueBy ?? 0) > 0
                  ? <span className="pill risk t-caption">{overdueBy}d overdue</span>
                  : <span className="pill flat t-caption">due {fmt.date(due)}</span>}
            </button>
          ))}
        </div>
      )}
    </Panel>
  );
}

function Numbers({ data }: { data: DashboardPayload }) {
  const { covered, of } = data.coverage;
  const pct = of ? Math.round((covered / of) * 100) : 0;
  const t = data.tiers;
  const total = t.Red + t.Amber + t.Green || 1;
  return (
    <div className="grid2">
      <Panel eyebrow="The number this company is about" title="Anchor coverage">
        <div className="t-display-xsmall">{of ? pct + '%' : '—'}</div>
        <p className="t-body-small muted">
          {fmt.num(covered)} of {fmt.num(of)} scored members are known by name to somebody
          here.
        </p>
        <div className="meter" style={{ marginTop: 10 }}>
          <i style={{ width: pct + '%', background: 'var(--good-fill)' }} />
        </div>
        <button type="button" className="linkish t-label-small" style={{ marginTop: 10 }}
          onClick={() => go('/segments')}>Who is known by nobody</button>
      </Panel>

      <Panel eyebrow="One model, one set of tiers" title="How the floor is doing">
        <div className="tiers">
          {(['Red', 'Amber', 'Green'] as const).map((k) => (
            <div className="tier" key={k}>
              <span className={'dot ' + (k === 'Red' ? 'risk' : k === 'Amber' ? 'warn' : 'good')} aria-hidden="true" />
              {/* fmt.num, like every other count on this screen. It read "1028" beside
                  "2,507" on a real-sized gym, which is the kind of thing seven seeded
                  members can never show. */}
              <span className="t-label-large">{fmt.num(t[k])}</span>
              <span className="t-caption muted">{k}</span>
            </div>
          ))}
        </div>
        <div className="tierbar" aria-hidden="true">
          <i className="risk" style={{ width: (t.Red / total) * 100 + '%' }} />
          <i className="warn" style={{ width: (t.Amber / total) * 100 + '%' }} />
          <i className="good" style={{ width: (t.Green / total) * 100 + '%' }} />
        </div>
        <p className="t-caption muted" style={{ marginTop: 10 }}>
          Scored members only.{' '}
          {data.onboardingCount === 1
            ? 'One more is inside their first 90 days and runs the member track instead'
            : `${data.onboardingCount} more are inside their first 90 days and run the member track instead`}
          , and{' '}
          {data.suppressedCount === 1
            ? 'one is on Care Track or Hold.'
            : `${data.suppressedCount} are on Care Track or Hold.`}
        </p>
      </Panel>
    </div>
  );
}
